import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../context/CompanyContext';
import { useUser } from '../context/UserContext';
import { useAd } from '../context/AdContext';
import { productsAPI, appointmentsAPI, ordersAPI } from '../api';
import { useFetch } from '../hooks/useFetch';
import { Card, Button, Badge, LoadingView, ErrorView, EmptyView, Modal, Toast, FieldLabel, FieldInput } from '../components/UI';
import { fmt } from '../utils/formatters';

function ServiceCard({ svc, onBook }) {
    return (
        <Card style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 6 }}>
                <div style={{ flex: 1 }}>
                    <p style={{
                        fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700,
                        color: 'var(--text-primary)', marginBottom: 2
                    }}>{svc.name}</p>
                    {svc.duration && <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>⏱ {svc.duration} min</p>}
                </div>
                <span style={{ color: 'var(--brand-300)', fontSize: 16, fontWeight: 700, fontFamily: 'var(--font-mono)' }}>
                    {fmt.money(svc.price, (svc.currency || 'usd').toUpperCase())}
                </span>
            </div>
            {svc.description && <p style={{ color: 'var(--text-muted)', fontSize: 13, lineHeight: 1.5, marginBottom: 10 }}>{svc.description}</p>}
            <Button label="Book" onClick={() => onBook(svc)} fullWidth />
        </Card>
    );
}

function ProductCard({ product, onOrder }) {
    const inStock = product.stock == null || product.stock > 0;
    return (
        <Card style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 6, gap: 8 }}>
                <div style={{ flex: 1 }}>
                    <p style={{
                        fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700,
                        color: 'var(--text-primary)', marginBottom: 4
                    }}>{product.name}</p>
                    {product.category && <Badge label={product.category} />}
                </div>
                <span style={{ color: 'var(--brand-300)', fontSize: 16, fontWeight: 700, fontFamily: 'var(--font-mono)' }}>
                    {fmt.money(product.price, (product.currency || 'usd').toUpperCase())}
                </span>
            </div>
            {product.description && <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 8, lineHeight: 1.5 }}>{product.description}</p>}
            {product.stock != null && (
                <p style={{ color: inStock ? 'var(--brand-300)' : 'var(--danger)', fontSize: 12, marginBottom: 8 }}>
                    {inStock ? `${product.stock} in stock` : 'Out of stock'}
                </p>
            )}
            <Button label={inStock ? 'Order' : 'Unavailable'} onClick={() => onOrder(product)} disabled={!inStock} fullWidth />
        </Card>
    );
}

function BookingModal({ visible, service, onClose, onConfirm }) {
    const { profile } = useUser();
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [name, setName] = useState(profile.name || '');
    const [email, setEmail] = useState(profile.email || '');
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);

    React.useEffect(() => {
        if (visible) { setDate(''); setTime(''); setName(profile.name || ''); setEmail(profile.email || ''); setNotes(''); setLoading(false); }
    }, [visible]);

    const handleBook = async () => {
        if (!date || !time) { Toast.show({ type: 'error', text1: 'Enter Date and Time' }); return; }
        setLoading(true);
        await onConfirm({ service, date, time, name, email, notes });
        setLoading(false);
    };

    return (
        <Modal visible={visible} onClose={onClose} title={`Book ${service?.name || ''}`}>
            <p style={{ color: 'var(--brand-300)', fontSize: 13, marginBottom: 12, marginTop: -8 }}>
                {fmt.money(service?.price, (service?.currency || 'usd').toUpperCase())}
                {service?.duration ? ` · ${service.duration} min` : ''}
            </p>
            <FieldLabel>Date (YYYY-MM-DD)</FieldLabel>
            <FieldInput value={date} onChange={e => setDate(e.target.value)} placeholder="2026-05-17" />
            <FieldLabel>Time (HH:MM)</FieldLabel>
            <FieldInput value={time} onChange={e => setTime(e.target.value)} placeholder="14:00" />
            <FieldLabel>Your Name</FieldLabel>
            <FieldInput value={name} onChange={e => setName(e.target.value)} placeholder="John Smith" />
            <FieldLabel>Email (For confirmation)</FieldLabel>
            <FieldInput value={email} onChange={e => setEmail(e.target.value)} placeholder="you@yourdomain.com" type="email" />
            <FieldLabel>Notes (Optional)</FieldLabel>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Any special requests?"
                style={{
                    display: 'block', width: '100%', backgroundColor: 'var(--bg-input)',
                    border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px',
                    color: 'var(--text-primary)', fontSize: 15, resize: 'vertical', minHeight: 72,
                    fontFamily: 'var(--font-body)', marginTop: 4
                }} />
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <Button label="Cancel" variant="outline" onClick={onClose} style={{ flex: 1 }} />
                <Button label="Book Now" onClick={handleBook} loading={loading} style={{ flex: 1 }} />
            </div>
        </Modal>
    );
}

function OrderModal({ visible, product, onClose, onConfirm }) {
    const { profile } = useUser();
    const [qty, setQty] = useState('1');
    const [name, setName] = useState(profile.name || '');
    const [email, setEmail] = useState(profile.email || '');
    const [type, setType] = useState('pickup');
    const [address, setAddress] = useState('');
    const [loading, setLoading] = useState(false);

    React.useEffect(() => {
        if (visible) { setQty('1'); setName(profile.name || ''); setEmail(profile.email || ''); setType('pickup'); setAddress(''); setLoading(false); }
    }, [visible]);

    const handleOrder = async () => {
        if (type === 'delivery' && !address.trim()) { Toast.show({ type: 'error', text1: 'Enter Delivery Address' }); return; }
        setLoading(true);
        await onConfirm({ product, quantity: parseInt(qty) || 1, name, email, type, deliveryAddress: type === 'delivery' ? address : null });
        setLoading(false);
    };

    return (
        <Modal visible={visible} onClose={onClose} title={`Order ${product?.name || ''}`}>
            <p style={{ color: 'var(--brand-300)', fontSize: 13, marginBottom: 12, marginTop: -8 }}>
                {fmt.money(product?.price, (product?.currency || 'usd').toUpperCase())} each
            </p>
            <FieldLabel>Quantity</FieldLabel>
            <FieldInput value={qty} onChange={e => setQty(e.target.value)} type="number" min="1" />
            <FieldLabel>Your Name</FieldLabel>
            <FieldInput value={name} onChange={e => setName(e.target.value)} placeholder="John Smith" />
            <FieldLabel>Email</FieldLabel>
            <FieldInput value={email} onChange={e => setEmail(e.target.value)} placeholder="you@yourdomain.com" type="email" />
            <FieldLabel>Fulfillment</FieldLabel>
            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                {['pickup', 'delivery'].map(t => (
                    <button key={t} onClick={() => setType(t)} style={{
                        flex: 1, padding: '10px', borderRadius: 10, border: '1px solid',
                        borderColor: type === t ? 'var(--brand-500)' : 'var(--border)',
                        backgroundColor: type === t ? 'var(--brand-700)' : 'var(--bg-input)',
                        color: type === t ? '#fff' : 'var(--text-muted)',
                        fontSize: 14, fontWeight: 600, cursor: 'pointer',
                    }}>{t === 'pickup' ? '🏪 Pickup' : '🚗 Delivery'}</button>
                ))}
            </div>
            {type === 'delivery' && (
                <>
                    <FieldLabel>Delivery Address</FieldLabel>
                    <FieldInput value={address} onChange={e => setAddress(e.target.value)} placeholder="123 Main St, City" />
                </>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <Button label="Cancel" variant="outline" onClick={onClose} style={{ flex: 1 }} />
                <Button label="Place Order" onClick={handleOrder} loading={loading} style={{ flex: 1 }} />
            </div>
        </Modal>
    );
}

export default function ServicesScreen() {
    const navigate = useNavigate();
    const { company } = useCompany();
    const { profile, updateProfile } = useUser();
    const { maybeShowAd } = useAd();

    const [tab, setTab] = useState('services');
    const [bookTarget, setBookTarget] = useState(null);
    const [orderTarget, setOrderTarget] = useState(null);

    const services = company?.services || [];
    const { data: productData, loading: prodLoading, error: prodError, refetch } =
        useFetch(() => productsAPI.list(company?.companyId), [company?.companyId]);
    const products = productData?.items || productData?.products || [];

    const handleBook = async ({ service, date, time, name, email, notes }) => {
        try {
            if (name) updateProfile({ name });
            if (email) updateProfile({ email });
            const { data } = await appointmentsAPI.create({
                companyId: company.companyId, service: service.name, date, time,
                callerName: name, callerEmail: email, callerPhone: profile.phone || null,
                notes, calledFrom: 'web',
            });
            setBookTarget(null);
            navigate('/booking-confirm', {
                state: {
                    type: 'appointment', id: data.appointmentId,
                    data: { service: service.name, date, time, name, email },
                    confirmationNumber: data.confirmationNumber, paymentUrl: data.paymentUrl
                },
            });
        } catch (err) {
            Toast.show({ type: 'error', text1: err.response?.data?.error || 'Booking Failed' });
        }
    };

    const handleOrder = async ({ product, quantity, name, email, type, deliveryAddress }) => {
        try {
            if (name) updateProfile({ name });
            if (email) updateProfile({ email });
            const { data } = await ordersAPI.create({
                companyId: company.companyId,
                items: [{ productId: product.productId, name: product.name, quantity, unitPrice: product.price }],
                fulfillmentType: type, deliveryAddress,
                callerName: name, callerEmail: email, callerPhone: profile.phone || null,
                paymentMethod: 'link', calledFrom: 'web',
            });
            setOrderTarget(null);
            navigate('/booking-confirm', {
                state: {
                    type: 'order', id: data.orderId,
                    data: { product: product.name, quantity, type },
                    confirmationNumber: data.confirmationNumber, paymentUrl: data.payment?.checkoutUrl || null
                },
            });
        } catch (err) {
            Toast.show({ type: 'error', text1: err.response?.data?.error || 'Order Failed' });
        }
    };

    const TABS = [{ key: 'services', label: '📋 Services' }, { key: 'products', label: '🛍 Products' }];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Tab bar */}
            <div style={{
                display: 'flex', padding: 8, gap: 6, backgroundColor: 'var(--bg-card)',
                borderBottom: '1px solid var(--border)', flexShrink: 0
            }}>
                {TABS.map(t => (
                    <button key={t.key} onClick={() => { setTab(t.key); maybeShowAd(t.key === 'services' ? 'appointments' : 'orders'); }}
                        style={{
                            flex: 1, padding: '8px', borderRadius: 8, border: 'none', cursor: 'pointer',
                            backgroundColor: tab === t.key ? 'var(--brand-500)' : 'transparent',
                            color: tab === t.key ? '#fff' : 'var(--text-muted)',
                            fontSize: 14, fontWeight: 600, fontFamily: 'var(--font-body)',
                        }}>{t.label}</button>
                ))}
            </div>

            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
                {tab === 'services' && (
                    services.length === 0
                        ? <EmptyView emoji="📋" title="No Services Listed" subtitle="Ask about Services in the Chat" />
                        : services.map(svc => <ServiceCard key={svc.name} svc={svc} onBook={setBookTarget} />)
                )}
                {tab === 'products' && (
                    prodLoading ? <LoadingView label="Loading Products…" />
                        : prodError ? <ErrorView message={prodError} onRetry={refetch} />
                            : products.length === 0 ? <EmptyView emoji="🛍" title="No Products Listed" />
                                : products.map(p => <ProductCard key={p.productId} product={p} onOrder={setOrderTarget} />)
                )}
            </div>

            <BookingModal visible={!!bookTarget} service={bookTarget} onClose={() => setBookTarget(null)} onConfirm={handleBook} />
            <OrderModal visible={!!orderTarget} product={orderTarget} onClose={() => setOrderTarget(null)} onConfirm={handleOrder} />
        </div>
    );
}
